import { FC, useState } from "react";
import type { GetReviewPerformanceResponse, ReviewPerformanceCell, ReviewPerformanceRow } from "@/models/responses";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { Alert, AlertDescription } from "@/lib/shadcn/ui/alert";
import { Badge } from "@/lib/shadcn/ui/badge";
import { Skeleton } from "@/lib/shadcn/ui/skeleton";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import { cn } from "@/lib/shadcn/utils";
import MeasureToggle from "@/lib/organisms/measure-toggle";
import UnavailableCell from "@/lib/organisms/unavailable-cell";
import ScoreChangeText from "@/lib/molecules/scoreChangeText";
import CohortInfoPopover from "@/lib/organisms/cohort-info-popover";
import { defaultMeasureValue, MeasureValue } from "@/lib/organisms/measure";

// The Airbnb review performance table (airbnb-review-performance-table AS-12/14/15).
// Owns the selected MeasureValue; every cell renders through the measure switch,
// composing MeasureToggle / UnavailableCell / ScoreChangeText. The two-tier blank
// taxonomy lives here: Tier-1 (rowStatus="no_data") mutes the whole row and shows a
// "No data" badge; Tier-2 blanks are reasoned em-dashes via UnavailableCell.

interface Category {
    // The ReviewPerformanceRow field carrying the cell.
    key: keyof Pick<
        ReviewPerformanceRow,
        "overall" | "cleanliness" | "accuracy" | "communication" | "location" | "checkIn" | "value"
    >;
    // Lower-cased testid suffix (note checkIn -> checkin).
    testid: string;
    label: string;
}

const CATEGORIES: Category[] = [
    { key: "overall", testid: "overall", label: "Overall" },
    { key: "cleanliness", testid: "cleanliness", label: "Cleanliness" },
    { key: "accuracy", testid: "accuracy", label: "Accuracy" },
    { key: "communication", testid: "communication", label: "Communication" },
    { key: "location", testid: "location", label: "Location" },
    { key: "checkIn", testid: "checkin", label: "Check-in" },
    { key: "value", testid: "value", label: "Value" },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Split a date-only "YYYY-MM-DD" string into its parts. Deliberately does NOT use
// new Date("YYYY-MM-DD") — that parses as UTC midnight and renders the previous
// day in UTC-negative zones (the established date-only-UTC gotcha).
function dateParts(isoDate: string): { day: string; month: string; year: number } | null {
    const [year, month, day] = isoDate.split("-").map((p) => Number.parseInt(p, 10));
    if (!year || !month || !day) {
        return null;
    }
    return { day: String(day).padStart(2, "0"), month: MONTHS[month - 1] ?? "", year };
}

/** Format a date-only "YYYY-MM-DD" string as "dd MMM yyyy". */
function formatObservedDate(isoDate: string): string {
    const parts = dateParts(isoDate);
    return parts ? `${parts.day} ${parts.month} ${parts.year}` : isoDate;
}

// The observation window as a single cell: "09 Feb → 09 Mar 2026". Every count on
// the row (New Reviews above all) accrued over exactly this span, and the scrape
// cadence is irregular enough that a reader cannot infer the start from the
// period alone — so the row states it rather than implying it. The shared year is
// printed once when both ends fall in it. With no baseline snapshot there is no
// window start, so the end date stands alone.
//
// `spoken` exists because screen readers vocalise U+2192 as "right arrow" (or
// skip it), which turns a date range into noise. The glyph is hidden from the
// accessibility tree and this phrasing is read instead.
function formatObservationWindow(
    baselineDate: string | null,
    observedDate: string,
): { visible: string; spoken: string } {
    const to = dateParts(observedDate);
    const from = baselineDate === null ? null : dateParts(baselineDate);
    if (!from || !to) {
        const single = formatObservedDate(observedDate);
        return { visible: single, spoken: `Observed ${single}` };
    }
    const fromLabel = from.year === to.year ? `${from.day} ${from.month}` : `${from.day} ${from.month} ${from.year}`;
    const toLabel = `${to.day} ${to.month} ${to.year}`;
    return { visible: `${fromLabel} → ${toLabel}`, spoken: `Observed from ${fromLabel} to ${toLabel}` };
}

const MEASURE_CAPTION_LABEL: Record<MeasureValue, string> = {
    "new-reviews": "New Reviews",
    cohort: "Cohort Score",
    lifetime: "Lifetime",
    "drift-prev-period": "Drift vs previous period",
    "drift-prev-year": "Drift vs previous year",
};

function score(value: number): string {
    return value.toFixed(2);
}

// Render one category cell's content for the selected measure. A real 0 always
// renders as a visible number; a null resolves to a reasoned Tier-2 em-dash.
function renderCellContent(measure: MeasureValue, cell: ReviewPerformanceCell, row: ReviewPerformanceRow) {
    const newReviews = row.newReviews ?? undefined;

    switch (measure) {
        case "new-reviews":
            return row.newReviews === null ? (
                <UnavailableCell reason={row.newReviewsReason ?? "no_data"} newReviews={newReviews} />
            ) : (
                <span>{row.newReviews}</span>
            );
        case "cohort":
            return cell.cohort === null ? (
                <UnavailableCell reason={cell.cohortReason ?? "no_data"} newReviews={newReviews} />
            ) : (
                <span>{score(cell.cohort)}</span>
            );
        case "lifetime":
            return cell.lifetime === null ? (
                <UnavailableCell reason="no_data" newReviews={newReviews} />
            ) : (
                <span>{score(cell.lifetime)}</span>
            );
        case "drift-prev-period":
            return cell.driftVsPrevPeriod === null ? (
                <UnavailableCell reason={cell.driftVsPrevPeriodReason ?? "no_comparison"} newReviews={newReviews} />
            ) : (
                <ScoreChangeText value={cell.driftVsPrevPeriod} />
            );
        case "drift-prev-year":
            return cell.driftVsPrevYear === null ? (
                <UnavailableCell reason={cell.driftVsPrevYearReason ?? "no_comparison"} newReviews={newReviews} />
            ) : (
                <ScoreChangeText value={cell.driftVsPrevYear} />
            );
        default:
            return null;
    }
}

// Splits the window into a visual form (with the arrow glyph) and a spoken form,
// so the arrow never reaches the accessibility tree.
const ObservationWindow: FC<{ baselineDate: string | null; observedDate: string }> = ({
    baselineDate,
    observedDate,
}) => {
    const { visible, spoken } = formatObservationWindow(baselineDate, observedDate);
    return (
        <>
            <span aria-hidden="true">{visible}</span>
            <span className="sr-only">{spoken}</span>
        </>
    );
};

interface ReviewPerformanceTableProps {
    data: GetReviewPerformanceResponse;
    loading?: boolean;
}

const SKELETON_ROWS = 4;
const STICKY_APARTMENT = "sticky left-0 bg-background z-10";

const ReviewPerformanceTable: FC<ReviewPerformanceTableProps> = ({ data, loading = false }) => {
    const [measure, setMeasure] = useState<MeasureValue>(defaultMeasureValue(data.showPreviousPeriod));

    const caption = `Review performance by category, ${data.periodLabel}, showing ${MEASURE_CAPTION_LABEL[measure]}`;

    const allNoData = data.rows.length > 0 && data.rows.every((r) => r.rowStatus === "no_data");

    return (
        <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <CardTitle>Performance · {data.periodLabel}</CardTitle>
                    <CohortInfoPopover />
                </div>
                <MeasureToggle
                    value={measure}
                    onValueChange={setMeasure}
                    showPreviousPeriod={data.showPreviousPeriod}
                    loading={loading}
                />
            </CardHeader>
            <CardContent className="min-w-0">
                {allNoData && (
                    <Alert className="mb-4">
                        <AlertDescription>No review snapshots were recorded in this period.</AlertDescription>
                    </Alert>
                )}
                <div
                    className="min-w-0 overflow-x-auto"
                    tabIndex={0}
                    role="region"
                    aria-label="Review performance table, scroll horizontally for more columns"
                >
                    {/* Screen-reader-only live status so the in-flight fetch is announced. */}
                    <span className="sr-only" role="status" aria-live="polite">
                        {loading ? "Loading review performance…" : ""}
                    </span>
                    <Table data-testid="review-performance-table" aria-busy={loading}>
                        <TableCaption data-testid="review-performance-caption" aria-live="polite">
                            {caption}
                        </TableCaption>
                        <TableHeader>
                            <TableRow>
                                <TableHead scope="col" className={STICKY_APARTMENT}>
                                    Apartment
                                </TableHead>
                                <TableHead scope="col">Observed</TableHead>
                                <TableHead scope="col" className="text-right">
                                    Reviews
                                </TableHead>
                                <TableHead scope="col" className="text-right">
                                    New
                                </TableHead>
                                {CATEGORIES.map((category) => (
                                    <TableHead key={category.key} scope="col" className="text-right">
                                        {category.label}
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading
                                ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                                      <TableRow key={`skeleton-${i}`} data-testid="review-table-skeleton">
                                          <TableCell className={STICKY_APARTMENT}>
                                              <Skeleton className="h-4 w-24" />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton className="h-4 w-28" />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton className="ml-auto h-4 w-8" />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton className="ml-auto h-4 w-8" />
                                          </TableCell>
                                          {CATEGORIES.map((category) => (
                                              <TableCell key={category.key}>
                                                  <Skeleton className="ml-auto h-4 w-10" />
                                              </TableCell>
                                          ))}
                                      </TableRow>
                                  ))
                                : data.rows.map((row) => {
                                      const noData = row.rowStatus === "no_data";
                                      return (
                                          <TableRow
                                              key={row.apartmentId}
                                              data-testid={`review-row-${row.apartmentId}`}
                                              data-row-status={row.rowStatus}
                                              className={cn(noData && "bg-muted/40 text-muted-foreground")}
                                          >
                                              <TableHead
                                                  scope="row"
                                                  className={cn(
                                                      STICKY_APARTMENT,
                                                      noData && "bg-muted/40",
                                                      "font-medium",
                                                  )}
                                              >
                                                  {row.apartmentName}
                                              </TableHead>
                                              <TableCell data-testid={`review-observed-${row.apartmentId}`}>
                                                  {noData || row.observedDate === null ? (
                                                      <Badge
                                                          variant="secondary"
                                                          data-testid="review-observed-no-data-badge"
                                                      >
                                                          No data
                                                      </Badge>
                                                  ) : (
                                                      <ObservationWindow
                                                          baselineDate={row.baselineDate}
                                                          observedDate={row.observedDate}
                                                      />
                                                  )}
                                              </TableCell>
                                              {/* Both counts stay visible in every
                                                  measure: a Lifetime score without its
                                                  volume, or a Drift without the new
                                                  reviews that moved it, is not
                                                  actionable. */}
                                              <TableCell
                                                  data-testid={`review-total-reviews-${row.apartmentId}`}
                                                  className="text-right tabular-nums"
                                              >
                                                  {row.totalReviews === null ? (
                                                      // No reason code exists for this
                                                      // count on the wire, so it gets the
                                                      // generic blank rather than a
                                                      // silent em-dash.
                                                      <UnavailableCell reason="no_data" />
                                                  ) : (
                                                      row.totalReviews
                                                  )}
                                              </TableCell>
                                              <TableCell
                                                  data-testid={`review-new-reviews-${row.apartmentId}`}
                                                  className="text-right tabular-nums"
                                              >
                                                  {row.newReviews === null ? (
                                                      <UnavailableCell
                                                          reason={row.newReviewsReason ?? "no_data"}
                                                      />
                                                  ) : (
                                                      row.newReviews
                                                  )}
                                              </TableCell>
                                              {CATEGORIES.map((category) => (
                                                  <TableCell
                                                      key={category.key}
                                                      data-testid={`review-cell-${row.apartmentId}-${category.testid}`}
                                                      className="text-right"
                                                  >
                                                      {renderCellContent(measure, row[category.key], row)}
                                                  </TableCell>
                                              ))}
                                          </TableRow>
                                      );
                                  })}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};

export default ReviewPerformanceTable;
